import { FormGroup } from '@angular/forms';

export const FORM_FIELD_TYPE_PASSWORD = 'password';

export function trimTrailingSlashes(path: string): string {
  let normalized = path;
  while (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  return normalized || '/';
}

export function isPasswordFieldType(type: string | undefined | null): boolean {
  return type === FORM_FIELD_TYPE_PASSWORD;
}

export function resolvePasswordFromFieldValues(
  fields: Array<{ field?: { type?: string; id?: string | number } }>,
  values: Record<string, unknown>,
): string {
  const passwordField = fields.find((item) => isPasswordFieldType(item.field?.type));
  if (!passwordField?.field?.id) {
    return '';
  }

  const value = values[String(passwordField.field.id)];
  return typeof value === 'string' ? value : '';
}

export function resolveCreateMemberPassword(
  form: FormGroup,
  fields: Array<{
    field_id?: string | number;
    id?: string | number;
    field?: { type?: string; id?: string | number };
  }>,
): string {
  const passwordField = fields.find((item) => isPasswordFieldType(item.field?.type));
  if (!passwordField) {
    return '';
  }

  const controlKey = String(
    passwordField.field_id ?? passwordField.field?.id ?? passwordField.id ?? '',
  );
  const value = controlKey ? form.get(controlKey)?.value : undefined;
  return typeof value === 'string' ? value : '';
}

export function isValidEmailAddress(email: string): boolean {
  const atIndex = email.indexOf('@');
  if (atIndex <= 0) {
    return false;
  }

  const domain = email.slice(atIndex + 1);
  const dotIndex = domain.lastIndexOf('.');
  return dotIndex > 0 && dotIndex < domain.length - 1;
}

export function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function extractStarRatingFromOptions(options: string): string {
  const starIndex = options.lastIndexOf('*');
  if (starIndex <= 0) {
    return 'N/A';
  }

  let start = starIndex - 1;
  while (start >= 0 && options[start] >= '0' && options[start] <= '9') {
    start -= 1;
  }

  const rating = options.slice(start + 1, starIndex + 1);
  return rating.length > 1 && rating.endsWith('*') ? rating : 'N/A';
}

export function extractHotelNameFromOptions(options: string): string {
  const rating = extractStarRatingFromOptions(options);
  if (rating === 'N/A') {
    return options;
  }

  const suffix = ` ${rating}`;
  return options.endsWith(suffix) ? options.slice(0, -suffix.length).trim() : options.trim();
}

export function hashStringToHexColor(seed: string): string {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = Math.trunc(hash * 31 + (seed.codePointAt(index) ?? 0));
  }

  const color = (Math.abs(hash) % 0xffffff).toString(16).padStart(6, '0');
  return `#${color}`;
}

export function stripParentheticalText(value: string): string {
  let result = '';
  let depth = 0;

  for (const char of value) {
    if (char === '(') {
      depth += 1;
      continue;
    }
    if (char === ')') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth === 0) {
      result += char;
    }
  }

  return result.replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
}

export function sanitizeInitialToken(token: string): string {
  let start = 0;
  let end = token.length;

  while (start < end) {
    const code = token.codePointAt(start) ?? 0;
    if (
      (code >= 48 && code <= 57) ||
      (code >= 65 && code <= 90) ||
      (code >= 97 && code <= 122)
    ) {
      break;
    }
    start += 1;
  }

  while (end > start) {
    const code = token.codePointAt(end - 1) ?? 0;
    if (
      (code >= 48 && code <= 57) ||
      (code >= 65 && code <= 90) ||
      (code >= 97 && code <= 122)
    ) {
      break;
    }
    end -= 1;
  }

  return token.slice(start, end);
}
